import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PRODUCT_CATEGORY_LABELS_IT,
  PRODUCT_STATUS_LABELS_IT,
  type ProductCategory,
  type ProductStatus,
} from '@produzionejb/types';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@produzionejb/ui';
import { useDeleteProduct, useProducts } from '../../lib/products-api';

const PAGE_SIZE = 50;

export function ProductListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState<ProductCategory | ''>('');
  const [status, setStatus] = useState<ProductStatus | ''>('');
  const [page, setPage] = useState(1);

  // Debounce search 300ms
  if (typeof window !== 'undefined') {
    // simple inline debounce via setTimeout pattern (mounted handler)
  }

  function handleSearchChange(v: string) {
    setSearch(v);
    setPage(1);
    // debounce naive
    window.clearTimeout((handleSearchChange as unknown as { t: number }).t);
    (handleSearchChange as unknown as { t: number }).t = window.setTimeout(() => {
      setDebouncedSearch(v.trim());
    }, 300);
  }

  const { data, isLoading, error } = useProducts({
    page,
    pageSize: PAGE_SIZE,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(category && { category }),
    ...(status && { status }),
  });

  const deleteMut = useDeleteProduct();

  async function handleDelete(id: string, name: string) {
    if (!window.confirm(t('products.list.confirmDelete', { name }))) return;
    try {
      await deleteMut.mutateAsync(id);
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">{t('products.list.title')}</h2>
        <Button onClick={() => navigate('/products/new')}>{t('products.list.newButton')}</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('products.list.filtersTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-4">
          <div>
            <label className="text-xs text-slate-500">{t('products.list.searchLabel')}</label>
            <Input
              placeholder={t('products.list.searchPlaceholder')}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">{t('products.list.categoryLabel')}</label>
            <select
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value as ProductCategory | '');
                setPage(1);
              }}
            >
              <option value="">{t('common.all')}</option>
              {Object.entries(PRODUCT_CATEGORY_LABELS_IT).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">{t('products.list.statusLabel')}</label>
            <select
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as ProductStatus | '');
                setPage(1);
              }}
            >
              <option value="">{t('common.all')}</option>
              {Object.entries(PRODUCT_STATUS_LABELS_IT).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <span className="text-sm text-slate-600">
              {data ? t('products.list.totalCount', { count: data.total }) : ''}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading && <p className="p-4 text-sm text-slate-500">{t('common.loading')}</p>}
          {error && (
            <p className="p-4 text-sm text-red-600">
              {t('products.list.errorLoad')}: {(error as Error).message}
            </p>
          )}
          {data && data.items.length === 0 && (
            <p className="p-4 text-sm text-slate-500">{t('products.list.empty')}</p>
          )}
          {data && data.items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-4 py-2 text-right">ID</th>
                  <th className="px-4 py-2">{t('products.fields.code')}</th>
                  <th className="px-4 py-2">{t('products.fields.name')}</th>
                  <th className="px-4 py-2">{t('products.fields.category')}</th>
                  <th className="px-4 py-2">{t('products.fields.unit')}</th>
                  <th className="px-4 py-2 text-right">{t('products.fields.vatRate')}</th>
                  <th className="px-4 py-2">{t('products.fields.status')}</th>
                  <th className="px-4 py-2 text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2 text-right font-mono text-xs text-slate-500">
                      {p.driveLegendId ?? '—'}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{p.code}</td>
                    <td className="px-4 py-2 font-medium text-slate-900">{p.name}</td>
                    <td className="px-4 py-2 text-slate-700">
                      {PRODUCT_CATEGORY_LABELS_IT[p.category]}
                    </td>
                    <td className="px-4 py-2 text-slate-700">{p.unit}</td>
                    <td className="px-4 py-2 text-right text-slate-700">{p.vatRate.toFixed(2)}%</td>
                    <td className="px-4 py-2">
                      <span
                        className={
                          p.status === 'ACTIVE'
                            ? 'rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700'
                            : p.status === 'INACTIVE'
                              ? 'rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700'
                              : 'rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-600'
                        }
                      >
                        {PRODUCT_STATUS_LABELS_IT[p.status]}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Link
                        to={`/products/${p.id}/edit`}
                        className="mr-3 text-brand-700 hover:underline"
                      >
                        {t('common.edit')}
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleDelete(p.id, p.name)}
                        className="text-red-600 hover:underline"
                        disabled={deleteMut.isPending}
                      >
                        {t('common.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {data && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ‹
          </Button>
          <span className="text-sm text-slate-600">
            {t('products.list.pageOf', { page, totalPages })}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            ›
          </Button>
        </div>
      )}
    </div>
  );
}
